import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import type { CashFlowSection } from '@accounting/shared';
import { useCashFlow } from '../../hooks/useReports';
import { CONSOLIDATED, useActiveSelection } from '../../stores/useCompanyStore';
import type { DrillTarget } from '../../components/reports/DrilldownModal';
import { ExportButtons } from '../../components/reports/ExportButtons';
import { DateRangePicker } from '../../components/reports/PeriodFilter';
import { ComparePicker } from '../../components/reports/ComparePicker';
import { RefreshButton } from '../../components/reports/RefreshButton';
import { formatAccountingDate, formatCurrency, todayIso } from '../../lib/formatters';
import {
  computeComparePeriod,
  formatMoneyDelta,
  formatPctChange,
  type ComparePreset,
} from '../../lib/comparePeriod';

export const Route = createFileRoute('/_auth/reports/cash-flow')({
  component: CashFlowPage,
});

const yearStart = () => `${new Date().getFullYear()}-01-01`;

function dayBefore(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

function CashFlowPage() {
  const selection = useActiveSelection();
  const navigate = useNavigate();
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(todayIso());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [comparePreset, setComparePreset] = useState<ComparePreset>('none');
  const { data, isLoading, error, refetch, isFetching } = useCashFlow(selection, from, to);
  const comparePeriod = useMemo(
    () => computeComparePeriod(comparePreset, { from, to }),
    [comparePreset, from, to],
  );
  const { data: compareData } = useCashFlow(
    selection,
    comparePeriod?.from ?? '',
    comparePeriod?.to ?? '',
  );
  const compareOn = !!comparePeriod && !!compareData;

  const openDrilldown = (target: DrillTarget, opts: { from?: string; to?: string } = {}): void =>
    void navigate({
      to: '/reports/drilldown',
      search: {
        codes: target.codes.join(','),
        label: target.label,
        from: opts.from,
        to: opts.to,
        back: '/reports/cash-flow',
      },
    });

  const toggleCollapsed = (key: string): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const allSectionKeys = ['operating', 'investing', 'financing'];

  if (!selection) return <NoCompany />;

  return (
    <div>
      <Link to="/reports" style={{ fontSize: 13 }}>← All reports</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>
          Cash Flow
          {selection === CONSOLIDATED && <span className="tag" style={{ marginLeft: 8 }}>Consolidated</span>}
        </h1>
        <div style={{ fontSize: 13, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className="btn-secondary"
            style={{ fontSize: 12, padding: '4px 10px' }}
            onClick={() => setCollapsed(new Set(allSectionKeys))}
            title="Hide line detail; show only the Net cash from each section"
          >
            Collapse all
          </button>
          <button
            className="btn-secondary"
            style={{ fontSize: 12, padding: '4px 10px' }}
            onClick={() => setCollapsed(new Set())}
            title="Show every line"
          >
            Expand all
          </button>
          <DateRangePicker from={from} to={to} onChange={(r) => { setFrom(r.from); setTo(r.to); }} />
          <ComparePicker value={comparePreset} onChange={setComparePreset} mode="range" />
          <RefreshButton onRefresh={() => void refetch()} isFetching={isFetching} />
          <ExportButtons selection={selection} type="cash_flow" params={{ from, to }} />
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load report.</p>}

      {data && (
        <div className="card" style={{ padding: 20 }}>
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            {formatAccountingDate(data.from)} – {formatAccountingDate(data.to)} · indirect method
          </p>

          {compareOn && comparePeriod && (
            <CompareHeader
              currentLabel={`${formatAccountingDate(data.from)} – ${formatAccountingDate(data.to)}`}
              compareLabel={comparePeriod.label}
            />
          )}
          <SectionBlock section={data.operating} compareSection={compareData?.operating} sectionKey="operating" onDrill={(t) => openDrilldown(t, { from, to })} collapsed={collapsed.has('operating')} onToggle={toggleCollapsed} compareOn={compareOn} />
          <SectionBlock section={data.investing} compareSection={compareData?.investing} sectionKey="investing" onDrill={(t) => openDrilldown(t, { from, to })} collapsed={collapsed.has('investing')} onToggle={toggleCollapsed} compareOn={compareOn} />
          <SectionBlock section={data.financing} compareSection={compareData?.financing} sectionKey="financing" onDrill={(t) => openDrilldown(t, { from, to })} collapsed={collapsed.has('financing')} onToggle={toggleCollapsed} compareOn={compareOn} />

          <Row
            label="Net change in cash"
            amount={data.netChangeInCash}
            compareAmount={compareData?.netChangeInCash}
            compareOn={compareOn}
            bold
            border
            onDrill={
              data.cashCodes.length > 0
                ? () => openDrilldown({ codes: data.cashCodes, label: 'Net change in cash' }, { from, to })
                : undefined
            }
          />
          <Row
            label="Beginning cash"
            amount={data.beginningCash}
            compareAmount={compareData?.beginningCash}
            compareOn={compareOn}
            onDrill={
              data.cashCodes.length > 0
                ? () => openDrilldown({ codes: data.cashCodes, label: 'Beginning cash' }, { to: dayBefore(from) })
                : undefined
            }
          />
          <Row
            label="Ending cash"
            amount={data.endingCash}
            compareAmount={compareData?.endingCash}
            compareOn={compareOn}
            bold
            onDrill={
              data.cashCodes.length > 0
                ? () => openDrilldown({ codes: data.cashCodes, label: 'Ending cash' }, { to })
                : undefined
            }
          />

          <div style={{ marginTop: 12 }}>
            {data.reconciled ? (
              <span className="tag ok">ties to change in cash</span>
            ) : (
              <span className="tag bad">DOES NOT RECONCILE</span>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

function SectionBlock({
  section,
  compareSection,
  sectionKey,
  onDrill,
  collapsed,
  onToggle,
  compareOn,
}: {
  section: CashFlowSection;
  compareSection?: CashFlowSection;
  sectionKey: string;
  onDrill: (t: DrillTarget) => void;
  collapsed: boolean;
  onToggle: (key: string) => void;
  compareOn: boolean;
}) {
  const sectionCodes = [...new Set(section.lines.flatMap((l) => l.codes))];
  /** Cash flow lines have no accountId — match the compare line by label. */
  const findCompare = (label: string): string | undefined =>
    compareSection?.lines.find((cl) => cl.label === label)?.amount;

  return (
    <div style={{ marginBottom: 14 }}>
      <h2
        style={{
          fontSize: 15,
          margin: '0 0 4px',
          borderBottom: '1px solid var(--border)',
          paddingBottom: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          cursor: section.lines.length > 0 ? 'pointer' : 'default',
        }}
        onClick={() => section.lines.length > 0 && onToggle(sectionKey)}
        title={section.lines.length > 0 ? (collapsed ? `Expand ${section.label}` : `Collapse ${section.label}`) : undefined}
      >
        {section.lines.length > 0 && (
          <span className="muted" style={{ fontSize: 10, width: 10, display: 'inline-block', textAlign: 'center' }}>
            {collapsed ? '▶' : '▼'}
          </span>
        )}
        {section.label}
      </h2>
      {section.lines.length === 0 && (
        <div className="muted" style={{ fontSize: 13, padding: '2px 0' }}>None</div>
      )}
      {!collapsed &&
        section.lines.map((l, i) => (
          <Row
            key={`${section.label}-${i}`}
            label={l.label}
            amount={l.amount}
            compareAmount={findCompare(l.label)}
            compareOn={compareOn}
            onDrill={l.codes.length > 0 ? () => onDrill({ codes: l.codes, label: l.label }) : undefined}
          />
        ))}
      <Row
        label={`Net cash from ${section.label.toLowerCase()}`}
        amount={section.total}
        compareAmount={compareSection?.total}
        compareOn={compareOn}
        bold
        onDrill={
          sectionCodes.length > 0
            ? () => onDrill({ codes: sectionCodes, label: `Net cash from ${section.label.toLowerCase()}` })
            : undefined
        }
      />
    </div>
  );
}

function CompareHeader({ currentLabel, compareLabel }: { currentLabel: string; compareLabel: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 11,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        paddingBottom: 4,
        marginBottom: 6,
        borderBottom: '1px solid var(--border)',
      }}
    >
      <span />
      <div style={{ display: 'flex', gap: 12 }}>
        <span style={{ minWidth: 100, textAlign: 'right' }}>{currentLabel}</span>
        <span style={{ minWidth: 100, textAlign: 'right' }}>{compareLabel}</span>
        <span style={{ minWidth: 110, textAlign: 'right' }}>$ Δ</span>
        <span style={{ minWidth: 64, textAlign: 'right' }}>% Δ</span>
      </div>
    </div>
  );
}

function Row({
  label,
  amount,
  compareAmount,
  compareOn,
  bold,
  border,
  onDrill,
}: {
  label: string;
  amount: string;
  compareAmount?: string;
  compareOn?: boolean;
  bold?: boolean;
  border?: boolean;
  onDrill?: () => void;
}) {
  const fw = bold ? 700 : 400;
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontWeight: fw,
        borderTop: border ? '2px solid var(--border)' : undefined,
        marginTop: border ? 12 : 0,
        paddingTop: border ? 10 : 2,
      }}
    >
      <span>{label}</span>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
        {onDrill ? (
          <button className="amount-link" style={{ fontWeight: fw }} onClick={onDrill}>
            {formatCurrency(amount, true)}
          </button>
        ) : (
          <span style={{ fontWeight: fw }}>{formatCurrency(amount, true)}</span>
        )}
        {compareOn && (
          <>
            <span style={{ minWidth: 100, textAlign: 'right', fontWeight: fw, color: 'var(--text-muted)' }}>
              {compareAmount !== undefined ? formatCurrency(compareAmount, true) : '—'}
            </span>
            <span style={{ minWidth: 110, textAlign: 'right', fontSize: 12 }}>
              {compareAmount !== undefined ? formatMoneyDelta(amount, compareAmount) : ''}
            </span>
            <span style={{ minWidth: 64, textAlign: 'right', fontSize: 12 }}>
              {compareAmount !== undefined ? formatPctChange(amount, compareAmount) : ''}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function NoCompany() {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 32 }}>
      <p className="muted">No active company.</p>
      <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
        Go to Companies
      </Link>
    </div>
  );
}
